/**
 * Redux slice for managing work order scheduling
 * Handles CRUD operations for work orders and their scheduling
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientMutation, clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";
import { resetALL } from "../../../../../.globals/redux/actions";

const INIT_STATE = {
    scheduleWorkOrders: {
        items: [],
        total: 0
    },
    scheduleWorkOrdersLoading: false,
    scheduleWorkOrdersError: null,
    scheduleWorkOrdersTotal: 0,
}

const route = "modules/workOrders/views/schedule/redux/slice";

/**
 * Fetches scheduled work orders with pagination and filters
 */
export const getScheduleWorkOrders = createAsyncThunk(
     "workOrders/getScheduleWorkOrders",
     async ({ openSearch, allDrivers, severity, statusId, driverIds, dateRange, typeId, tagIds, customerIds, limit, offset }, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                query searchDriversWithWorkOrders ($openSearch: String, $allDrivers: Boolean, $severity: String, $statusId: Int, $driverIds: [Int], $dateRange: AWSDateTimeRange!, $typeId: Int, $tagIds: [Int], $customerIds: [Int], $limit: Int, $offset: Int) {
                    data: searchDriversWithWorkOrders (openSearch: $openSearch, allDrivers: $allDrivers, severity: $severity, statusId: $statusId, driverIds: $driverIds, dateRange: $dateRange, typeId: $typeId, tagIds: $tagIds, customerIds: $customerIds, limit: $limit, offset: $offset)
                }
                `,
                {
                    openSearch,
                    allDrivers,
                    severity,
                    statusId,
                    driverIds,
                    dateRange,
                    typeId,
                    tagIds,
                    customerIds,
                    limit,
                    offset
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            
            const parsedData = JSON.parse(response?.data|| '{"items":[],"total":0}');
            return parsedData;
        } catch (error) {
            ExceptionManager(error, route, "getScheduleWorkOrders");
            return rejectWithValue(error.message);
        }
     }
)

/**
 * Updates work order duration
 */
export const updateWorkOrderDuration = createAsyncThunk(
    "workOrders/updateWorkOrderDuration",
    async (payload, { rejectWithValue }) => {
        try {
            const mutation = `mutation updateWorkOrder ($id: Int!, $estimatedDurationMins: Int) {
                updateWorkOrder (id: $id, estimatedDurationMins: $estimatedDurationMins) {
                    id
                    estimatedDurationMins
                }
            }`;

            const response = await clientMutation(
                mutation,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data;
        } catch (error) {
            ExceptionManager(error, route, "updateWorkOrderDuration");
            return rejectWithValue(error.message);
        }
    }
)

/**
 * Updates work order start time
 */
export const updateWorkOrderStart = createAsyncThunk(
    "workOrders/updateWorkOrderStart",
    async (payload, { rejectWithValue }) => {
        try {
            const mutation = `mutation updateWorkOrder ($id: Int!, $scheduledDate: String, $estimatedDurationMins: Int) {
                updateWorkOrder (id: $id, scheduledDate: $scheduledDate, estimatedDurationMins: $estimatedDurationMins) {
                    id
                    scheduledDate
                    estimatedDurationMins
                }
            }`;

            const response = await clientMutation(
                mutation,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data;
        } catch (error) {
            ExceptionManager(error, route, "updateWorkOrderStart");
            return rejectWithValue(error.message);
        }
    }
)

/**
 * Updates work order driver assignment
 */
export const updateWorkOrderChangeDriver = createAsyncThunk(
    "workOrders/updateWorkOrderChangeDriver",
    async (payload, {rejectWithValue}) => {
        try {   
            if (!payload.driverId) {
                return rejectWithValue("Driver ID is required");
            }

            const formattedPayload = {
                ...payload,
                scheduledDate: payload.scheduledDate ? new Date(payload.scheduledDate).toISOString() : null
            };

            const mutation = `mutation updateWorkOrder ($id: Int!, $driverId: Int, $scheduledDate: String, $estimatedDurationMins: Int) {
                updateWorkOrder (id: $id, driverId: $driverId, scheduledDate: $scheduledDate, estimatedDurationMins: $estimatedDurationMins) {
                    id
                    driverId
                    scheduledDate
                    estimatedDurationMins
                }
            }`;
            
            const response = await clientMutation(
                mutation,
                formattedPayload,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data;
        } catch (error) {
            ExceptionManager(error, route, "updateWorkOrderChangeDriver");
            return rejectWithValue(error);
        }
    }
)

/**
 * Updates work order schedule details
 */
export const updateScheduleWorkOrder = createAsyncThunk(
    "workOrders/updateScheduleWorkOrder",
    async (payload, { rejectWithValue }) => {
        try {
            const mutation = `mutation updateWorkOrder ($id: Int!, $customerId: Int, $customerLocationId: Int, $customerContactId: Int, $workOrderTypeId: Int, $description: String, $driverId: Int, $scheduledDate: String, $closedDate: String, $estimatedDurationMins: Int, $severity: String, $statusId: Int, $statusUpdatedOn: String) {
            updateWorkOrder (id: $id, customerId: $customerId, customerLocationId: $customerLocationId, customerContactId: $customerContactId, workOrderTypeId: $workOrderTypeId, description: $description, driverId: $driverId, scheduledDate: $scheduledDate, closedDate: $closedDate, estimatedDurationMins: $estimatedDurationMins, severity: $severity, statusId: $statusId, statusUpdatedOn: $statusUpdatedOn) {
                id
                customerId
                customer {
                    id
                    name
                    ein
                    contactId
                    locationId
                    tagIds
                    createdOn
                    createdBy
                    updatedOn
                    updatedBy
                }
                customerLocationId
                customerLocation {
                    id
                    name
                    customerId
                    locationType
                    contact
                    address
                    latitude
                    longitude
                    createdOn
                    createdBy
                    updatedOn
                    updatedBy
                }
                customerContactId
                customerContact {
                    id
                    entityId
                    entityName
                    name
                    phone
                    email
                    notifyByPhone
                    notifyByEmail
                    countryCode
                    createdOn
                    createdBy
                    updatedOn
                    updatedBy
                }
                workOrderTypeId
                workOrderType {
                    id
                    name
                    description
                    dynamicFormId
                    estimatedDurationMins
                    createdOn
                    createdBy
                    updatedOn
                    updatedBy
                }
                workOrderNumber
                description
                driverId
                driver {
                    id
                    externalId
                    authId
                    title
                    name
                    tagIds
                    driverId
                    companyId
                    contactInfo
                    idTimeZone
                    status
                    driverScheduledTasksCount
                    avatarFileId
                    tempAvatarUrl
                    permissions
                    dateformat
                    isKph
                    supervisorId
                }
                scheduledDate
                closedDate
                estimatedDurationMins
                severity
                statusId
                status {
                    id
                    name
                    description
                    companyId
                    color
                    category
                    isDefault
                    createdOn
                    createdBy
                    updatedOn
                    updatedBy
                }
                statusUpdatedOn
                createdOn
                createdBy
                updatedOn
                updatedBy
            }
        }`;

            const response = await clientMutation(
                mutation,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data;   
        } catch (error) {
            ExceptionManager(error, route, "updateScheduleWorkOrder");
            return rejectWithValue(error.message);
        }
    }
)

/**
 * Deletes a scheduled work order
 */
export const deleteScheduleWorkOrder = createAsyncThunk(
    "workOrders/deleteScheduleWorkOrder",
    async (payload, { rejectWithValue }) => {
        try {
            const mutation = `mutation deleteWorkOrder ($id: Int!) {
                deleteWorkOrder (id: $id)
            }`;

            const response = await clientMutation(
                mutation,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data;
        } catch (error) {
            ExceptionManager(error, route, "deleteScheduleWorkOrder");
            return rejectWithValue(error.message);
        }
    }
)

const scheduleWorkOrdersSlice = createSlice({
    name: "workOrders/scheduleWorkOrders",
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //getScheduleWorkOrders
        builder.addCase(getScheduleWorkOrders.pending, (state) => {
            state.scheduleWorkOrdersLoading = true;
        });
        //getScheduleWorkOrders fulfilled
        builder.addCase(getScheduleWorkOrders.fulfilled, (state, action) => {
            state.scheduleWorkOrdersLoading = false;
            const isFilteredDriver = action.meta.arg?.driverId?.length > 0;
            // Accumulate items for pagination
            const { items = [], total = 0 } = action.payload;
            const { offset = 0 } = action.meta.arg;
            if (offset === 0 && !isFilteredDriver) {
                // First page: replace items
                state.scheduleWorkOrders.items = items;
            } else {
                // Subsequent pages: append items but preserve work orders that have been modified
                const existingItems = state.scheduleWorkOrders.items;
                
                // Identificar IDs de los elementos que ya existen para evitar duplicados
                const existingItemIds = new Set(existingItems.map(item => item.id));
                
                // Filtrar los nuevos elementos para evitar duplicados
                const filteredNewItems = items.filter(item => !existingItemIds.has(item.id));
                
                // Combinar elementos existentes y nuevos
                state.scheduleWorkOrders.items = [...existingItems, ...filteredNewItems];
            }
            // Update total count
            state.scheduleWorkOrders.total = total;
        });
        builder.addCase(getScheduleWorkOrders.rejected, (state, action) => {
            state.scheduleWorkOrdersLoading = false;
            state.scheduleWorkOrdersError = action.payload;
        });

        // Caso para updateWorkOrderDuration
        builder.addCase(updateWorkOrderDuration.fulfilled, (state, action) => {
            if (action.payload) {
                const { id, estimatedDurationMins } = action.payload;
                // Actualizar la work order en el array de items
                state.scheduleWorkOrders.items = state.scheduleWorkOrders.items.map(driver => {
                    // Si el driver tiene workOrders, actualizar la que coincida con el id
                    if (driver.workOrders) {
                        driver.workOrders = driver.workOrders.map(order => {
                            if (order.id === id) {
                                return { ...order, estimatedDurationMins };
                            }
                            return order;
                        });
                    }
                    return driver;
                });
            }
        });
        builder.addCase(updateWorkOrderDuration.rejected, (state, action) => {
            state.scheduleWorkOrdersError = action.payload;
        });

        // Caso para updateWorkOrderStart
        builder.addCase(updateWorkOrderStart.fulfilled, (state, action) => {
            if (action.payload) {
                const { id, scheduledDate, estimatedDurationMins } = action.payload;
                // Actualizar la work order en el array de items
                state.scheduleWorkOrders.items = state.scheduleWorkOrders.items.map(driver => {
                    // Si el driver tiene workOrders, actualizar la que coincida con el id
                    if (driver.workOrders) {
                        driver.workOrders = driver.workOrders.map(order => {
                            if (order.id === id) {
                                return { 
                                    ...order, 
                                    scheduledDate,
                                    estimatedDurationMins,
                                    // Asegurarnos de que el updated_start se preserva para que la UI lo reconozca
                                    // en futuras cargas de datos
                                    updated_start: true, 
                                    start_time: scheduledDate
                                };
                            }
                            return order;
                        });
                    }
                    return driver;
                });
            }
        });
        builder.addCase(updateWorkOrderStart.rejected, (state, action) => {
            state.scheduleWorkOrdersError = action.payload;
        });

        // Caso para updateWorkOrderChangeDriver
        builder.addCase(updateWorkOrderChangeDriver.fulfilled, (state, action) => {
            if (action.payload) {
                const { id, driverId, scheduledDate, estimatedDurationMins } = action.payload;
                
                // 1. Encontrar el driver origen y remover la orden
                let workOrderToMove = null;
                let sourceDriverId = null;
                
                // Primero encontrar la orden y su driver actual
                state.scheduleWorkOrders.items.forEach(driver => {
                    if (driver.workOrders) {
                        const foundOrder = driver.workOrders.find(order => order.id === id);
                        if (foundOrder) {
                            workOrderToMove = {...foundOrder, 
                                driverId, 
                                scheduledDate, 
                                estimatedDurationMins,
                                updated_driver: true
                            };
                            sourceDriverId = driver.id;
                        }
                    }
                });
                
                // 2. Eliminar la orden del driver origen
                if (sourceDriverId) {
                    state.scheduleWorkOrders.items = state.scheduleWorkOrders.items.map(driver => {
                        if (driver.id === sourceDriverId) {
                            return {
                                ...driver,
                                workOrders: driver.workOrders.filter(order => order.id !== id)
                            };
                        }
                        return driver;
                    });
                }
                
                // 3. Agregar la orden al nuevo driver
                if (workOrderToMove) {
                    state.scheduleWorkOrders.items = state.scheduleWorkOrders.items.map(driver => {
                        if (driver.id === driverId) {
                            return {
                                ...driver,
                                workOrders: [...(driver.workOrders || []), workOrderToMove]
                            };
                        }
                        return driver;
                    });
                }
            }
        });

        builder.addCase(updateWorkOrderChangeDriver.rejected, (state, action) => {
            state.scheduleWorkOrdersError = action.payload?.error;
        });

        //updateScheduleWorkOrder
        builder.addCase(updateScheduleWorkOrder.pending, (state) => {
            state.scheduleWorkOrdersLoading = true;
        });
        builder.addCase(updateScheduleWorkOrder.fulfilled, (state, action) => {
            state.scheduleWorkOrdersLoading = false;
            state.scheduleWorkOrders = action.payload;
        });
        builder.addCase(updateScheduleWorkOrder.rejected, (state, action) => {
            state.scheduleWorkOrdersLoading = false;
            state.scheduleWorkOrdersError = action.payload;
        });

        //deleteScheduleWorkOrder
        builder.addCase(deleteScheduleWorkOrder.pending, (state) => {
            state.scheduleWorkOrdersLoading = true;
        });
        builder.addCase(deleteScheduleWorkOrder.fulfilled, (state, action) => {
            state.scheduleWorkOrdersLoading = false;
            state.scheduleWorkOrders = action.payload;
        });
        builder.addCase(deleteScheduleWorkOrder.rejected, (state, action) => {
            state.scheduleWorkOrdersLoading = false;
            state.scheduleWorkOrdersError = action.payload;
        });

    }   
})

export const { actions, reducer } = scheduleWorkOrdersSlice;

export const { reset } = actions;

export default reducer;



